import { useState, useRef, useEffect } from "react";
import "@/App.css";
import { motion } from "framer-motion";
import { 
  Heart, Cake, Briefcase, TrendingUp, Package, Sparkles, 
  Car, GraduationCap, User, Play, Phone, Mail,
  Menu, X, MessageCircle, ArrowRight,
  Instagram, Facebook, ExternalLink, Users, Eye, Zap, Award
} from "lucide-react";
import { reelsData, servicesData, testimonialsData, clientsData, adrunServices } from "./data/reelsData";

// Assets
const TREELS_LOGO = "/images/T- Reels- F-B.png";
const ADRUN_LOGO = "/images/ADRUN LOGO .png";

// Contact Info
const WHATSAPP_NUMBER = "+917013915387";
const EMAIL = "Treelsofficial2025@gmail.com";
const PHONE_1 = "+91 70139 15387";
const PHONE_2 = "+91 81216 37872";
const INSTAGRAM_URL = "https://www.instagram.com/treels_official";
const FACEBOOK_URL = "https://www.facebook.com/people/T-Reels/pfbid0qU2gxBBxDcNbYLdyVyStaXUXKUgQEUi9kE2H6vRHfw8KqhLsJBxqEuxgCFwAzQ2il";
const ADRUN_WEBSITE = "https://adrunadvertising.in/";
const ADRUN_INSTAGRAM = "https://www.instagram.com/adrun_advertising_media";
const DEFAULT_WA_TEXT = "Hi TREELS, I am interested in your services.";

// Founders Data
// const foundersData = [
//   {
//     name: "Abhilash",
//     role: "Co-Founder & Creative Director",
//     initial: "A",
//     description: "Digital innovation and creative direction. Brings cinematic vision to every project."
//   },
//   {
//     name: "Vijay Reddy",
//     role: "Co-Founder & Operations",
//     initial: "V",
//     description: "Operations expert ensuring excellence in campaign execution and client satisfaction."
//   },
//   {
//     name: "Saketh",
//     role: "Co-Founder & Content Lead",
//     initial: "S",
//     description: "Content strategy and brand storytelling. Master of visual narratives."
//   },
//   {
//     name: "Sai Harsha Reddy",
//     role: "Co-Founder & Strategy",
//     initial: "S",
//     description: "Strategic leader driving regional expansion and brand partnerships across India."
//   }
// ];

// Icon mapping for services
const iconMap = {
  Heart, Cake, Briefcase, TrendingUp, Package, Sparkles, Car, GraduationCap, User
};

// Animation variants - minimal
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" }
};

// Header Component
const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { name: "About", href: "#about" },
    { name: "Services", href: "#services" },
    { name: "Our Work", href: "#work" },
    // { name: "Founders", href: "#founders" },
    { name: "Contact", href: "#contact" }
  ];

  return (
    <>
      <header className="header" data-testid="header">
        <nav className="nav container">
          <a href="#" data-testid="nav-logo">
            <img src={TREELS_LOGO} alt="TREELS" className="nav-logo" />
          </a>
          <ul className="nav-links">
            {navLinks.map((link) => (
              <li key={link.name}>
                <a href={link.href} data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}>
                  {link.name}
                </a>
              </li>
            ))}
          </ul>
          <a 
            href={`https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(DEFAULT_WA_TEXT)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="nav-cta"
            data-testid="nav-cta"
          >
            Get a Quote
          </a>
          <button 
            className="mobile-menu-btn" 
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu size={24} strokeWidth={1.5} />
          </button>
        </nav>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <div className="mobile-menu-header">
            <img src={TREELS_LOGO} alt="TREELS" className="nav-logo" />
            <button onClick={() => setMobileMenuOpen(false)} data-testid="mobile-menu-close">
              <X size={24} strokeWidth={1.5} />
            </button>
          </div>
          <ul className="mobile-menu-links">
            {navLinks.map((link) => (
              <li key={link.name}>
                <a href={link.href} onClick={() => setMobileMenuOpen(false)}>{link.name}</a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="hero" id="hero" data-testid="hero-section">
      <motion.div 
        className="hero-content"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
      >
        <span className="hero-label">Premium Reel Production</span>
        <h1 className="hero-headline" data-testid="hero-headline">
          We Craft Stunning Reels —<br />
          Delivered <em>the Same Day</em>
        </h1>
        <p className="hero-subheadline" data-testid="hero-subheadline">
          From intimate celebrations to grand corporate events — we shoot, edit, and deliver premium reels before your day ends.
        </p>
        <div className="hero-ctas">
          <a 
            href={`https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(DEFAULT_WA_TEXT)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary"
            data-testid="hero-cta-whatsapp"
          >
            <MessageCircle size={18} strokeWidth={1.5} />
            Get a Quote
          </a>
          <a href="#work" className="btn btn-secondary" data-testid="hero-cta-work">
            View Projects
            <ArrowRight size={16} strokeWidth={1.5} />
          </a>
        </div>
        
        {/* Stats */}
        <div className="hero-stats">
          <div className="hero-stat">
            <div className="hero-stat-number">500<span>+</span></div>
            <div className="hero-stat-label">Reels Delivered</div>
          </div>
          <div className="hero-stat">
            <div className="hero-stat-number">100<span>+</span></div>
            <div className="hero-stat-label">Happy Clients</div>
          </div>
          <div className="hero-stat">
            <div className="hero-stat-number">10<span>+</span></div>
            <div className="hero-stat-label">Cities Covered</div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section className="section" id="about" data-testid="about-section">
      <div className="container">
        <motion.div 
          className="about-text"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-80px" }}
          variants={{
            animate: { transition: { staggerChildren: 0.1 } }
          }}
        >
          <motion.div variants={fadeInUp}>
            <span className="section-label">Our Story</span>
            <h2>Born from Friendship,<br /><em>Built on Vision</em></h2>
            <p>
              TREELS was founded by a group of college friends who shared more than memories — they shared ambition. Growing up together, they witnessed how special moments often faded before they could be captured and shared.
            </p>
            <p>
              Instead of choosing separate paths, they united with a single mission: to deliver premium, cinematic reels before the day ends. Because memories deserve to be shared while they're still alive.
            </p>
            
            <div className="about-quote">
              <p>"We don't just create reels — we preserve moments and deliver them instantly."</p>
            </div>
            
            <div className="about-values">
              <div className="about-value">
                <div className="about-value-icon">
                  <Users size={20} strokeWidth={1.5} />
                </div>
                <h4>Unity</h4>
                <p>Built on friendship</p>
              </div>
              <div className="about-value">
                <div className="about-value-icon">
                  <Eye size={20} strokeWidth={1.5} />
                </div>
                <h4>Vision</h4>
                <p>Creating visibility</p>
              </div>
              <div className="about-value">
                <div className="about-value-icon">
                  <Zap size={20} strokeWidth={1.5} />
                </div>
                <h4>Speed</h4>
                <p>Same-day delivery</p>
              </div>
              <div className="about-value">
                <div className="about-value-icon">
                  <Award size={20} strokeWidth={1.5} />
                </div>
                <h4>Excellence</h4>
                <p>Premium quality</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Founders Section
// const FoundersSection = () => {
//   return (
//     <section className="section section--alt" id="founders" data-testid="founders-section">
//       <div className="container">
//         <motion.div 
//           className="section-header"
//           initial="initial"
//           whileInView="animate"
//           viewport={{ once: true }}
//           variants={fadeInUp}
//         >
//           <span className="section-label">The Team</span>
//           <h2 className="section-title">Meet the Visionaries</h2>
//           <p className="section-subtitle">
//             Founders & driving force behind TREELS
//           </p>
//         </motion.div>

//         <motion.div 
//           className="founders-intro"
//           initial="initial"
//           whileInView="animate"
//           viewport={{ once: true }}
//           variants={fadeInUp}
//         >
//           <p>
//             What started as an idea between friends became a powerful creative force delivering high-impact reels across India. United by passion and driven by excellence, our founders bring diverse expertise to every project.
//           </p>
//         </motion.div>

//         <motion.div 
//           className="founders-grid"
//           initial="initial"
//           whileInView="animate"
//           viewport={{ once: true }}
//           variants={{
//             animate: { transition: { staggerChildren: 0.1 } }
//           }}
//         >
//           {foundersData.map((founder, index) => (
//             <motion.div 
//               key={index}
//               className="founder-card"
//               variants={fadeInUp}
//               data-testid={`founder-card-${index}`}
//             >
//               <div className="founder-avatar">{founder.initial}</div>
//               <h3>{founder.name}</h3>
//               <div className="founder-role">{founder.role}</div>
//               <p>{founder.description}</p>
//             </motion.div>
//           ))}
//         </motion.div>
//       </div>
//     </section>
//   );
// };

// Services Section
const ServicesSection = () => {
  return (
    <section className="section" id="services" data-testid="services-section">
      <div className="container">
        <motion.div 
          className="section-header"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <span className="section-label">Services</span>
          <h2 className="section-title">What We Create</h2>
          <p className="section-subtitle">
            From concept to delivery, we craft high-impact reels that make moments unforgettable.
          </p>
        </motion.div>
        
        <motion.div 
          className="services-grid"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-40px" }}
          variants={{
            animate: { transition: { staggerChildren: 0.05 } }
          }}
        >
          {servicesData.map((service) => {
            const IconComponent = iconMap[service.icon];
            return (
              <motion.div 
                key={service.id} 
                className="service-card"
                variants={fadeInUp}
                data-testid={`service-card-${service.id}`}
              >
                <div className="service-icon">
                  <IconComponent size={24} strokeWidth={1.5} />
                </div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    { number: 1, title: "We Arrive", description: "Our team reaches your venue, fully equipped and ready." },
    { number: 2, title: "We Shoot", description: "Capturing every moment with cinematic precision." },
    { number: 3, title: "We Edit", description: "Same-day editing with premium quality output." },
    { number: 4, title: "You Receive", description: "Your reels delivered before the day ends." }
  ];

  return (
    <section className="section section--alt" data-testid="how-it-works-section">
      <div className="container">
        <motion.div 
          className="section-header"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <span className="section-label">Process</span>
          <h2 className="section-title">How It Works</h2>
          <p className="section-subtitle">
            Simple, efficient, and always on time — that's the TREELS way.
          </p>
        </motion.div>

        <motion.div 
          className="steps-container"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={{
            animate: { transition: { staggerChildren: 0.1 } }
          }}
        >
          {steps.map((step) => (
            <motion.div 
              key={step.number} 
              className="step"
              variants={fadeInUp}
              data-testid={`step-${step.number}`}
            >
              <div className="step-number">{step.number}</div>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Reels Showcase Section
const ReelsShowcaseSection = () => {

  return (
    <section className="section reels-showcase" id="work" data-testid="reels-section">
      <div className="container">
        <motion.div 
          className="section-header"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <span className="section-label">Portfolio</span>
          <h2 className="section-title">Our Work</h2>
          <p className="section-subtitle">
            High-impact reels that capture moments and tell stories.
          </p>
        </motion.div>

        <motion.div 
          className="reels-grid"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={{
            animate: { transition: { staggerChildren: 0.1 } }
          }}
        >
          {reelsData.map((reel, index) => (
            <motion.div 
              key={reel.id}
              className="reel-card"
              variants={fadeInUp}
              data-testid={`reel-card-${reel.id}`}
            >
              <video
                src={reel.videoUrl}
                controls
                controlsList="nodownload nofullscreen noremoteplayback"
                disablePictureInPicture
                loop
                playsInline
                preload="metadata"
                onContextMenu={(e) => e.preventDefault()} // Disable right-click
                style={{ pointerEvents: 'auto' }} // Ensure video is still clickable for play
              />
              <div className="reel-play-icon">
                <Play size={22} fill="currentColor" strokeWidth={0} />
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Trusted By Section
const TrustedBySection = () => {
  return (
    <section className="section section--alt trusted-by" data-testid="trusted-by-section">
      <div className="container">
        <motion.div 
          className="section-header"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <span className="section-label">Clients</span>
          <h2 className="section-title" style={{ fontSize: "1.75rem" }}>
            Trusted by Brands & Celebrations
          </h2>
        </motion.div>

        <motion.div 
          className="clients-grid"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={{
            animate: { transition: { staggerChildren: 0.05 } }
          }}
        >
          {clientsData.map((client) => (
            <motion.div 
              key={client.id}
              className="client-badge"
              variants={fadeInUp}
              data-testid={`client-${client.id}`}
            >
              {client.name}
            </motion.div>
          ))}
        </motion.div>

        <p className="trusted-by-note">
          Fast turnarounds. Premium quality. Same-day delivery across India.
        </p>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  return (
    <section className="section" id="testimonials" data-testid="testimonials-section">
      <div className="container">
        <motion.div 
          className="section-header"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <span className="section-label">Testimonials</span>
          <h2 className="section-title">What Clients Say</h2>
        </motion.div>

        <motion.div 
          className="testimonials-grid"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={{
            animate: { transition: { staggerChildren: 0.1 } }
          }}
        >
          {testimonialsData.map((testimonial) => (
            <motion.div 
              key={testimonial.id}
              className="testimonial-card"
              variants={fadeInUp}
              data-testid={`testimonial-${testimonial.id}`}
            >
              <p className="testimonial-quote">{testimonial.quote}</p>
              <p className="testimonial-author">{testimonial.name}</p>
              <p className="testimonial-event">{testimonial.event}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Statement Band Section
const StatementBandSection = () => {
  return (
    <section className="statement-band" data-testid="statement-band">
      <div className="container">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="statement-text">
            Not just reels — we deliver memories, instantly.
          </h2>
          <p className="statement-subtext">
            Share your story while the moment is still alive.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// AdRun Section
const AdRunSection = () => {
  return (
    <section className="section section--alt adrun-section" data-testid="adrun-section">
      <div className="container">
        <motion.div 
          className="adrun-content"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={{
            animate: { transition: { staggerChildren: 0.1 } }
          }}
        >
          <motion.div variants={fadeInUp}>
            <img src={ADRUN_LOGO} alt="AdRun Advertising" className="adrun-logo" />
          </motion.div>
          <motion.div className="adrun-text" variants={fadeInUp}>
            <h3>AdRun Advertising</h3>
            <p>
              Beyond reels, we help brands reach wider audiences through strategic advertising solutions across Telangana and India.
            </p>

            <div className="adrun-services">
              {adrunServices.map((service, index) => (
                <span key={index} className="adrun-service-tag">{service}</span>
              ))}
            </div>

            <div className="adrun-links">
              <a href={ADRUN_WEBSITE} target="_blank" rel="noopener noreferrer" data-testid="adrun-website-link">
                <ExternalLink size={14} strokeWidth={1.5} />
                Website
              </a>
              <a href={ADRUN_INSTAGRAM} target="_blank" rel="noopener noreferrer" data-testid="adrun-instagram-link">
                <Instagram size={14} strokeWidth={1.5} />
                Instagram
              </a>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    eventType: "",
    date: "",
    city: "",
    message: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const message = `Hi TREELS!%0A%0AName: ${formData.name}%0APhone: ${formData.phone}%0AEvent Type: ${formData.eventType}%0ADate: ${formData.date}%0ACity: ${formData.city}%0AMessage: ${formData.message}`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}?text=${message}`, "_blank");
  };

  return (
    <section className="section" id="contact" data-testid="contact-section">
      <div className="container">
        <motion.div 
          className="contact-content"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={{
            animate: { transition: { staggerChildren: 0.1 } }
          }}
        >
          <motion.div className="contact-info" variants={fadeInUp}>
            <span className="section-label">Contact</span>
            <h2>Let's Start Your Project</h2>
            <p>
              Ready to make your moments unforgettable? Contact us today.
            </p>
            
            <div className="contact-details">
              <div className="contact-item">
                <div className="contact-icon">
                  <MessageCircle size={20} strokeWidth={1.5} />
                </div>
                <div className="contact-item-text">
                  <span className="contact-item-label">WhatsApp</span>
                  <a href={`https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(DEFAULT_WA_TEXT)}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp">
                    {PHONE_1}
                  </a>
                </div>
              </div>
              
              <div className="contact-item">
                <div className="contact-icon">
                  <Mail size={20} strokeWidth={1.5} />
                </div>
                <div className="contact-item-text">
                  <span className="contact-item-label">Email</span>
                  <a href={`mailto:${EMAIL}`} data-testid="contact-email">{EMAIL}</a>
                </div>
              </div>
              
              <div className="contact-item">
                <div className="contact-icon">
                  <Phone size={20} strokeWidth={1.5} />
                </div>
                <div className="contact-item-text">
                  <span className="contact-item-label">Phone</span>
                  <a href={`tel:${PHONE_1.replace(/[^0-9+]/g, '')}`} data-testid="contact-phone-1">{PHONE_1}</a>
                  <a href={`tel:${PHONE_2.replace(/[^0-9+]/g, '')}`} data-testid="contact-phone-2">{PHONE_2}</a>
                </div>
              </div>
            </div>

            <div className="contact-socials">
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="social-link" data-testid="social-instagram">
                <Instagram size={20} strokeWidth={1.5} />
              </a>
              <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="social-link" data-testid="social-facebook">
                <Facebook size={20} strokeWidth={1.5} />
              </a>
            </div>
          </motion.div>

          <motion.div className="contact-form" variants={fadeInUp}>
            <form onSubmit={handleSubmit} data-testid="contact-form">
              <div className="form-row form-row--2col">
                <div className="form-group">
                  <label htmlFor="name">Your Name</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="form-name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    name="phone" 
                    placeholder="+91 98765 43210"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="form-phone"
                  />
                </div>
              </div>
              
              <div className="form-row form-row--2col">
                <div className="form-group">
                  <label htmlFor="eventType">Event Type</label>
                  <select 
                    id="eventType" 
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleChange}
                    required
                    data-testid="form-event-type"
                  >
                    <option value="">Select event type</option>
                    <option value="Wedding">Wedding</option>
                    <option value="Birthday">Birthday</option>
                    <option value="Corporate Event">Corporate Event</option>
                    <option value="Product Launch">Product Launch</option>
                    <option value="Pooja/Religious">Pooja/Religious</option>
                    <option value="Car Delivery">Car Delivery</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="date">Event Date</label>
                  <input 
                    type="date" 
                    id="date" 
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    data-testid="form-date"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="city">City</label>
                <input 
                  type="text" 
                  id="city" 
                  name="city" 
                  placeholder="Hyderabad"
                  value={formData.city}
                  onChange={handleChange}
                  data-testid="form-city"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="message">Message (Optional)</label>
                <textarea 
                  id="message" 
                  name="message" 
                  placeholder="Tell us about your event..."
                  value={formData.message}
                  onChange={handleChange}
                  data-testid="form-message"
                />
              </div>
              
              <div className="form-submit">
                <button type="submit" className="btn btn-primary" data-testid="form-submit-btn">
                  <MessageCircle size={18} strokeWidth={1.5} />
                  Send via WhatsApp
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer Section
const FooterSection = () => {
  return (
    <footer className="footer" data-testid="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-brand">
            <img src={TREELS_LOGO} alt="TREELS" className="footer-logo" />
            <p>
              Premium same-day reels for weddings, events, and brands across India.
            </p>
          </div>
          
          <div className="footer-links">
            <h4>Quick Links</h4>
            <ul>
              <li><a href="#about">About Us</a></li>
              <li><a href="#founders">Our Team</a></li>
              <li><a href="#services">Services</a></li>
              <li><a href="#work">Our Work</a></li>
              <li><a href="#contact">Contact</a></li>
            </ul>
          </div>
          
          <div className="footer-links">
            <h4>Connect</h4>
            <ul>
              <li><a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">Instagram</a></li>
              <li><a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer">Facebook</a></li>
              <li><a href={`https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(DEFAULT_WA_TEXT)}`} target="_blank" rel="noopener noreferrer">WhatsApp</a></li>
              <li><a href={ADRUN_WEBSITE} target="_blank" rel="noopener noreferrer">AdRun Advertising</a></li>
            </ul>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p className="footer-copyright">
            © 2025 TREELS. All rights reserved.
          </p>
          <p className="footer-copyright">
            Powered by Treels
          </p>
        </div>
      </div>
    </footer>
  );
};

// Floating WhatsApp Button
const WhatsAppFloat = () => {
  return (
    <a 
      href={`https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(DEFAULT_WA_TEXT)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-float"
      data-testid="whatsapp-float"
      aria-label="Chat on WhatsApp"
    >
      <MessageCircle size={28} fill="white" strokeWidth={0} />
    </a>
  );
};

// Main App Component
function App() {
  // Video download protection
  useEffect(() => {
    // Disable right-click context menu globally
    const handleContextMenu = (e) => {
      e.preventDefault();
    };

    // Disable common download keyboard shortcuts
    const handleKeyDown = (e) => {
      // Disable Ctrl+S (Save), Ctrl+Shift+I (DevTools), F12 (DevTools)
      if (
        (e.ctrlKey && e.key === 's') ||
        (e.ctrlKey && e.shiftKey && e.key === 'I') ||
        e.key === 'F12'
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup event listeners
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div className="App">
      <Header />
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <HowItWorksSection />
      <ReelsShowcaseSection />
      {/* <FoundersSection /> */}
      <TrustedBySection />
      <TestimonialsSection />
      <StatementBandSection />
      <AdRunSection />
      <ContactSection />
      <FooterSection />
      <WhatsAppFloat />
    </div>
  );
}

export default App;
